import { Mutation } from "@apollo/client/react/components";
import React, { useState } from "react";
import * as palette from "@govlaunch/palette";
/* eslint-disable-next-line */
import { MultiVendorSelect, MultiGovernmentSelect, ProductSelect } from "@govlaunch/select";
import { IProduct, IGovernment } from "~/types/types";
import { Button, Modal, ModalClose } from "@govlaunch/core";
import { SearchAlgoliaIndex } from "@govlaunch/algolia";
import mediaQueries from "~/components/mediaQueries";
import { IVendorQuery, IVendorQueryVariables } from "~/lib/queries/__generated__/VendorQuery.generated";
import InviteGovernmentsToAddProduct from "~/lib/mutations/InviteGovernmentsToAddProduct";
import { Margin } from "~/components/spacings";
import {
  IInviteGovernmentsToAddProductMutation,
  IInviteGovernmentsToAddProductMutationVariables,
} from "~/lib/mutations/__generated__/InviteGovernmentsToAddProduct.generated";
import VendorQuery from "~/lib/queries/VendorQuery";
import uniqBy from "lodash/fp/uniqBy";
import PendingGovernmentInvites from "~/components/vendor/profile/PendingGovernmentInvites";
import { FormattedMessage, useIntl } from "react-intl";

interface IInviteGovernmentsModalProps {
  vendor: NonNullable<IVendorQuery["company"]>;
  isOpen: boolean;
  onRequestClose: () => void;
}

export default function InviteGovernmentsModal({ vendor, isOpen, onRequestClose }: IInviteGovernmentsModalProps) {
  if (!isOpen) {
    return null;
  }

  const [selectedProduct, setSelectedProduct] = useState<Pick<IProduct, "_id" | "name" | "slug" | "logo"> | null>(null);
  const [selectedGovernments, setSelectedGovernments] = useState<Pick<IGovernment, "_id" | "name" | "logo" | "slug">[]>(
    [],
  );
  const [nextStep, setNextStep] = useState<boolean>(false);

  return (
    <Mutation<IInviteGovernmentsToAddProductMutation, IInviteGovernmentsToAddProductMutationVariables>
      mutation={InviteGovernmentsToAddProduct}
      update={(proxy, { data }) => {
        if (!data) {
          return;
        }

        proxy.writeQuery<IVendorQuery, IVendorQueryVariables>({
          query: VendorQuery,
          variables: {
            slug: vendor.slug,
            language: null,
          },
          data: {
            __typename: "Query",
            company: {
              ...vendor,
              pendingProductStackRequests: uniqBy("_id", [
                ...vendor.pendingProductStackRequests,
                ...data.inviteGovernmentsToAddProduct,
              ]),
            },
          },
        });
      }}
    >
      {(inviteGovernments, { data, loading }) => (
        <Modal
          ariaHideApp={false}
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          destroyOnClose={true}
          overlayStyle={{
            zIndex: 1000,
          }}
          style={{
            overflow: "visible",
          }}
        >
          <ModalClose onClick={onRequestClose} />

          {!(data && data.inviteGovernmentsToAddProduct && selectedProduct) ? (
            <div
              css={mediaQueries({
                width: [350, 350, 650, 650],
                padding: 32,
              })}
            >
              {!nextStep && (
                <SelectGovernmentsStep
                  selectedGovernments={selectedGovernments}
                  onSelectGovernments={setSelectedGovernments}
                  onCancel={onRequestClose}
                  onNext={() => setNextStep(true)}
                />
              )}

              {nextStep && (
                <SelectProductStep
                  confirming={loading}
                  onConfirm={() => {
                    if (selectedProduct && selectedGovernments.length > 0) {
                      inviteGovernments({
                        variables: {
                          governments: selectedGovernments.map(({ _id }) => _id),
                          productId: selectedProduct._id,
                        },
                      });
                    }
                  }}
                  selectedProduct={selectedProduct}
                  onSelectProduct={setSelectedProduct}
                  vendorProducts={vendor.products || []}
                  onCancel={onRequestClose}
                />
              )}
            </div>
          ) : (
            <div
              css={mediaQueries({
                width: [350, 350, 580, 580],
                padding: 32,
              })}
            >
              <PendingGovernmentInvites
                vendor={vendor}
                productStackRequests={vendor.pendingProductStackRequests.filter(
                  (request) => request.product._id === selectedProduct._id,
                )}
                product={selectedProduct}
                onRequestClose={onRequestClose}
              />
            </div>
          )}
        </Modal>
      )}
    </Mutation>
  );
}

interface ISelectGovernmentsStepProps {
  selectedGovernments: Pick<IGovernment, "_id" | "name" | "logo" | "slug">[];
  onSelectGovernments: (governments: Pick<IGovernment, "_id" | "name" | "logo" | "slug">[]) => void;
  onCancel: () => void;
  onNext: () => void;
}

function SelectGovernmentsStep({
  selectedGovernments,
  onSelectGovernments,
  onCancel,
  onNext,
}: ISelectGovernmentsStepProps) {
  const intl = useIntl();

  return (
    <div>
      <div>
        <p
          css={{
            fontSize: 18,
            fontWeight: "bold",
            color: palette.darkGray,
            margin: "0 0 4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span css={{ marginRight: 4 }}>
            <FormattedMessage defaultMessage="Add Your Partners" id="rxt9M4" />
          </span>
          <svg viewBox="0 0 64 64" width={22} height={22}>
            <path d="M1.652 19.564h17V62h-17zM44.862 19.564H62V62H44.862z" fill="#5aaae0" />
            <path d="M18.652 2h26.21v60h-26.21z" fill="#7dc7ff" />
            <path
              d="M33.912 48.566a3.923 3.923 0 0 0 1.894-3.343 4 4 0 0 0-8 0 3.923 3.923 0 0 0 1.893 3.343 5.922 5.922 0 0 0-3.893 5.539V62h12v-7.895a5.922 5.922 0 0 0-3.894-5.539z"
              fill="#f73445"
            />
            <g fill="#359ed8">
              <path d="M22.684 23.512H24.7v1.973h-2.016zM22.684 27.46H24.7v1.973h-2.016zM22.684 31.407H24.7v1.973h-2.016zM22.684 35.354H24.7v1.974h-2.016zM26.717 23.512h2.016v1.973h-2.016zM26.717 27.46h2.016v1.973h-2.016zM26.717 31.407h2.016v1.973h-2.016zM26.717 35.354h2.016v1.974h-2.016zM30.749 23.512h2.016v1.973H30.75zM30.749 27.46h2.016v1.973H30.75zM30.749 31.407h2.016v1.973H30.75zM30.749 35.354h2.016v1.974H30.75zM34.781 23.512h2.017v1.973H34.78zM26.717 19.564h2.016v1.974h-2.016zM30.749 19.564h2.016v1.974H30.75zM34.781 19.564h2.017v1.974H34.78zM34.781 27.46h2.017v1.973H34.78zM34.781 31.407h2.017v1.973H34.78zM34.781 35.354h2.017v1.974H34.78zM38.814 23.512h2.016v1.973h-2.016zM38.814 27.46h2.016v1.973h-2.016zM38.814 31.407h2.016v1.973h-2.016zM38.814 35.354h2.016v1.974h-2.016z" />
            </g>
            <path
              d="M13.47 52.485a2.93 2.93 0 0 0 1.166-2.328 3 3 0 0 0-6 0A2.93 2.93 0 0 0 9.8 52.485a4.93 4.93 0 0 0-3.165 4.58V62h10v-4.934a4.93 4.93 0 0 0-3.166-4.581z"
              fill="#f7f769"
            />
            <path
              d="M22.543 49.524a2.93 2.93 0 0 0 1.165-2.327 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.166 2.327 4.93 4.93 0 0 0-3.166 4.581V62h10v-7.895a4.93 4.93 0 0 0-3.165-4.581z"
              fill="#ffc943"
            />
            <path
              d="M53.794 52.485a2.93 2.93 0 0 0 1.166-2.328 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.165 2.328 4.93 4.93 0 0 0-3.166 4.58V62h10v-4.934a4.93 4.93 0 0 0-3.165-4.581z"
              fill="#f7f769"
            />
            <path
              d="M44.72 49.524a2.93 2.93 0 0 0 1.167-2.327 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.165 2.327 4.93 4.93 0 0 0-3.165 4.581V62h10v-7.895a4.93 4.93 0 0 0-3.166-4.581z"
              fill="#ffc943"
            />
            <path
              d="M49.903 26.472h2.016v15.79h-2.016zM55.951 26.472h2.017v15.79H55.95zM5.546 26.472h2.017v15.79H5.546zM11.595 26.472h2.016v15.79h-2.016z"
              fill="#005889"
            />
            <path d="M23.692 4.76h16.13v9.87h-16.13z" fill="#359ed8" />
          </svg>
        </p>
        <p
          css={{
            fontSize: 14,
            margin: 0,
            color: palette.mediumGray,
          }}
        >
          <FormattedMessage
            defaultMessage="Invite your government partner to verify that they use one of your solutions"
            id="jdSmPk"
          />
        </p>
      </div>

      <Margin mt={28}>
        <SearchAlgoliaIndex index="governments">
          {({ results: governments, searchValue: inputValue, onChange: onInputValueChange }: any) => (
            <MultiGovernmentSelect
              selectableGovernments={governments}
              css={{
                minWidth: 300,
              }}
              governments={selectedGovernments}
              inputValue={inputValue}
              onInputValueChange={onInputValueChange}
              onChange={onSelectGovernments}
              placeholder={intl.formatMessage({
                defaultMessage: "Find your government",
                id: "WAV/MK",
              })}
            />
          )}
        </SearchAlgoliaIndex>
      </Margin>

      <Margin mt={28}>
        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            color={palette.innovatorBlue}
            textColor={palette.white}
            disabled={selectedGovernments.length === 0}
            onClick={onNext}
          >
            <FormattedMessage defaultMessage="Next" id="9+Ddtu" />
          </Button>
          <Margin ml={8}>
            <a
              href="#"
              onClick={onCancel}
              css={{
                color: palette.mediumGray,
                fontSize: 14,
              }}
            >
              <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
            </a>
          </Margin>
        </div>
      </Margin>
    </div>
  );
}

interface ISelectProductStepProps {
  selectedProduct: Pick<IProduct, "_id" | "name" | "slug" | "logo"> | null;
  onSelectProduct: (product: Pick<IProduct, "_id" | "name" | "slug" | "logo">) => void;
  vendorProducts: Pick<IProduct, "_id" | "name" | "logo">[];
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}

function SelectProductStep({
  selectedProduct,
  onSelectProduct,
  vendorProducts,
  onCancel,
  onConfirm,
  confirming,
}: ISelectProductStepProps) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState<string>("");
  const products = vendorProducts.filter((product) => {
    if (!inputValue) {
      return true;
    }

    return product.name.toLowerCase().includes(inputValue.toLowerCase());
  });

  return (
    <div>
      <div>
        <p
          css={{
            fontSize: 18,
            fontWeight: "bold",
            color: palette.darkGray,
            margin: "0 0 4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span css={{ marginRight: 4 }}>
            <FormattedMessage defaultMessage="Select Product" id="5JlF3Y" />
          </span>
          <svg viewBox="0 0 64 64" width={22} height={22}>
            <path d="M1.652 19.564h17V62h-17zM44.862 19.564H62V62H44.862z" fill="#5aaae0" />
            <path d="M18.652 2h26.21v60h-26.21z" fill="#7dc7ff" />
            <path
              d="M33.912 48.566a3.923 3.923 0 0 0 1.894-3.343 4 4 0 0 0-8 0 3.923 3.923 0 0 0 1.893 3.343 5.922 5.922 0 0 0-3.893 5.539V62h12v-7.895a5.922 5.922 0 0 0-3.894-5.539z"
              fill="#f73445"
            />
            <g fill="#359ed8">
              <path d="M22.684 23.512H24.7v1.973h-2.016zM22.684 27.46H24.7v1.973h-2.016zM22.684 31.407H24.7v1.973h-2.016zM22.684 35.354H24.7v1.974h-2.016zM26.717 23.512h2.016v1.973h-2.016zM26.717 27.46h2.016v1.973h-2.016zM26.717 31.407h2.016v1.973h-2.016zM26.717 35.354h2.016v1.974h-2.016zM30.749 23.512h2.016v1.973H30.75zM30.749 27.46h2.016v1.973H30.75zM30.749 31.407h2.016v1.973H30.75zM30.749 35.354h2.016v1.974H30.75zM34.781 23.512h2.017v1.973H34.78zM26.717 19.564h2.016v1.974h-2.016zM30.749 19.564h2.016v1.974H30.75zM34.781 19.564h2.017v1.974H34.78zM34.781 27.46h2.017v1.973H34.78zM34.781 31.407h2.017v1.973H34.78zM34.781 35.354h2.017v1.974H34.78zM38.814 23.512h2.016v1.973h-2.016zM38.814 27.46h2.016v1.973h-2.016zM38.814 31.407h2.016v1.973h-2.016zM38.814 35.354h2.016v1.974h-2.016z" />
            </g>
            <path
              d="M13.47 52.485a2.93 2.93 0 0 0 1.166-2.328 3 3 0 0 0-6 0A2.93 2.93 0 0 0 9.8 52.485a4.93 4.93 0 0 0-3.165 4.58V62h10v-4.934a4.93 4.93 0 0 0-3.166-4.581z"
              fill="#f7f769"
            />
            <path
              d="M22.543 49.524a2.93 2.93 0 0 0 1.165-2.327 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.166 2.327 4.93 4.93 0 0 0-3.166 4.581V62h10v-7.895a4.93 4.93 0 0 0-3.165-4.581z"
              fill="#ffc943"
            />
            <path
              d="M53.794 52.485a2.93 2.93 0 0 0 1.166-2.328 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.165 2.328 4.93 4.93 0 0 0-3.166 4.58V62h10v-4.934a4.93 4.93 0 0 0-3.165-4.581z"
              fill="#f7f769"
            />
            <path
              d="M44.72 49.524a2.93 2.93 0 0 0 1.167-2.327 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.165 2.327 4.93 4.93 0 0 0-3.165 4.581V62h10v-7.895a4.93 4.93 0 0 0-3.166-4.581z"
              fill="#ffc943"
            />
            <path
              d="M49.903 26.472h2.016v15.79h-2.016zM55.951 26.472h2.017v15.79H55.95zM5.546 26.472h2.017v15.79H5.546zM11.595 26.472h2.016v15.79h-2.016z"
              fill="#005889"
            />
            <path d="M23.692 4.76h16.13v9.87h-16.13z" fill="#359ed8" />
          </svg>
        </p>
        <p
          css={{
            fontSize: 14,
            margin: 0,
            color: palette.mediumGray,
          }}
        >
          <FormattedMessage defaultMessage="Which product would you like to include in this invite?" id="oFUVi6" />
        </p>
      </div>

      <Margin mt={28}>
        <ProductSelect
          value={selectedProduct}
          css={{
            minWidth: 300,
          }}
          products={products}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
          onChange={onSelectProduct}
          placeholder={intl.formatMessage({
            defaultMessage: "Select your product",
            id: "g5mjtM",
          })}
        />
      </Margin>

      <Margin mt={28}>
        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            color={palette.innovatorBlue}
            textColor={palette.white}
            disabled={!selectedProduct || confirming}
            onClick={onConfirm}
          >
            {confirming ? (
              <FormattedMessage defaultMessage="Sending Invites" id="jE87Cf" />
            ) : (
              <FormattedMessage defaultMessage="Send Invites" id="NVxaQu" />
            )}
          </Button>
          <Margin ml={8}>
            <a
              href="#"
              onClick={onCancel}
              css={{
                color: palette.mediumGray,
                fontSize: 14,
              }}
            >
              <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
            </a>
          </Margin>
        </div>
      </Margin>
    </div>
  );
}
